import type { Component } from 'vue';
import { Ban, Eye, EyeOff } from 'lucide-vue-next';
import { CUSTOM_LIST_STATUS, LOCATION_ZONE_CUSTOM_PLAYLIST_STATUS_FILTER_OPTIONS } from '@/constants/lookupFilterOptions';

export interface CustomListStatus {
    id: number;
    /** The status's name, such as "Disabled". */
    label: string;
    icon: Component;
    /** The icon's colour class. */
    tone: string;
}

/**
 * An eye for "Show in Station Switcher", a struck-out eye for "Don't Show in
 * Station Switcher", and a red ban sign for "Disabled". Players skip a disabled
 * list, so that one is drawn in the destructive colour.
 */
const ICON_AND_TONE_BY_STATUS_ID: Record<number, { icon: Component; tone: string }> = {
    [CUSTOM_LIST_STATUS.SHOW_IN_SWITCHER]: { icon: Eye, tone: 'text-muted-foreground' },
    [CUSTOM_LIST_STATUS.DONT_SHOW_IN_SWITCHER]: { icon: EyeOff, tone: 'text-muted-foreground' },
    [CUSTOM_LIST_STATUS.DISABLED]: { icon: Ban, tone: 'text-destructive' },
};

/** A custom list's status, or null for a row with no status or one this list does not know. */
export function customListStatusFor(statusId: unknown): CustomListStatus | null {
    const id = Number(statusId);
    const option = LOCATION_ZONE_CUSTOM_PLAYLIST_STATUS_FILTER_OPTIONS.find((candidate) => candidate.id === id);
    const iconAndTone = ICON_AND_TONE_BY_STATUS_ID[id];
    return option && iconAndTone ? { id, label: option.display_string, ...iconAndTone } : null;
}

/**
 * A custom list's name with its status after it, as "Lobby Mix (Disabled)". The headings use it
 * for their tooltip, so the tooltip says what the icon and the screen reader say.
 */
export function nameWithCustomListStatus(name: string, statusId: unknown): string {
    const status = customListStatusFor(statusId);
    return status === null ? name : `${name} (${status.label})`;
}
